/**
 * Auth Debug Panel
 * 
 * Shows authentication and capability state for debugging.
 * Only visible when ?debug=1 is in the URL.
 */

import { useCapabilities } from './CapabilityProvider';
import { useEffect, useState } from 'react';

export default function AuthDebugPanel() {
  const { me, loading, error, has } = useCapabilities();
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    if (me) {
      setLastUpdated(new Date().toISOString());
    }
  }, [me]);

  const urlParams = new URLSearchParams(window.location.search);
  const isDebug = urlParams.get('debug') === '1';

  if (!isDebug) {
    return null;
  }

  // Sanitize payload for display (remove sensitive data)
  const sanitizePayload = (data) => {
    if (!data) return null;
    const sanitized = { ...data };
    // Remove any sensitive fields if needed
    return sanitized;
  };

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Auth Debug Panel</h3>
      <div style={styles.content}>
        <div style={styles.section}>
          <strong>Build Info:</strong>
          <div style={styles.value}>{me?.buildStamp || 'unknown'}</div>
        </div>
        
        <div style={styles.section}>
          <strong>Actor ID:</strong>
          <div style={styles.value}>{me?.actorId || me?.id || '(not loaded)'}</div>
        </div>
        
        <div style={styles.section}>
          <strong>Is Super Admin:</strong>
          <div style={styles.value}>{me?.isSuperAdmin ? 'true' : 'false'}</div>
        </div>
        
        <div style={styles.section}>
          <strong>Capability Count:</strong>
          <div style={styles.value}>
            {me?.capabilities?.length || me?.capabilityCount || 0}
            {me?.isSuperAdmin && (!me?.capabilities || me.capabilities.length === 0) && (
              <span style={styles.warning}> ⚠️ Super admin has no capabilities!</span>
            )}
          </div>
        </div>
        
        <div style={styles.section}>
          <strong>Loading:</strong>
          <div style={styles.value}>{loading ? 'true' : 'false'}</div>
        </div>
        
        <div style={styles.section}>
          <strong>Error:</strong>
          <div style={styles.value}>
            {error ? `${error.status || 'unknown'}: ${error.message || 'unknown'}` : 'none'}
          </div>
        </div>
        
        {lastUpdated && (
          <div style={styles.section}>
            <strong>Last Updated:</strong>
            <div style={styles.value}>{lastUpdated}</div>
          </div>
        )}
        
        {me?.capabilities && me.capabilities.length > 0 && (
          <div style={styles.section}>
            <strong>Capabilities (first 10):</strong>
            <div style={styles.capsList}>
              {me.capabilities.slice(0, 10).map(cap => (
                <div key={cap} style={styles.capItem}>{cap}</div>
              ))}
              {me.capabilities.length > 10 && (
                <div style={styles.capItem}>... and {me.capabilities.length - 10} more</div>
              )}
            </div>
          </div>
        )}
        
        <div style={styles.section}>
          <strong>Raw /admin/me Payload (sanitized):</strong>
          <pre style={styles.payload}>
            {JSON.stringify(sanitizePayload(me), null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    position: 'fixed',
    bottom: '1rem',
    right: '1rem',
    width: '400px',
    maxHeight: '80vh',
    overflow: 'auto',
    backgroundColor: '#fff',
    border: '2px solid #3b82f6',
    borderRadius: '0.5rem',
    padding: '1rem',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    zIndex: 9999,
    fontSize: '0.75rem',
  },
  title: {
    fontSize: '1rem',
    fontWeight: 'bold',
    marginBottom: '0.75rem',
    color: '#111827',
    borderBottom: '1px solid #d1d5db',
    paddingBottom: '0.5rem',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  value: {
    fontFamily: 'monospace',
    fontSize: '0.7rem',
    color: '#374151',
    wordBreak: 'break-word',
  },
  warning: {
    color: '#dc2626',
    fontWeight: 'bold',
  },
  capsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    maxHeight: '150px',
    overflow: 'auto',
  },
  capItem: {
    fontFamily: 'monospace',
    fontSize: '0.65rem',
    color: '#6b7280',
    padding: '0.125rem 0.25rem',
    backgroundColor: '#f3f4f6',
    borderRadius: '0.125rem',
  },
  payload: {
    fontSize: '0.65rem',
    backgroundColor: '#f9fafb',
    padding: '0.5rem',
    borderRadius: '0.25rem',
    overflow: 'auto',
    maxHeight: '200px',
    margin: 0,
    fontFamily: 'monospace',
  },
};

